import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Search,
  MoreHorizontal,
  Mail,
  Phone,
  Building2,
  ArrowUpRight,
  Star,
  Users,
  TrendingUp,
  DollarSign,
  Sparkles,
  FileText,
  ArrowRightCircle,
} from 'lucide-react';
import { Card } from '../components/Card';
import { Badge } from '../components/Badge';
import { Button, IconButton } from '../components/Button';
import { StatCard } from '../components/StatCard';
import { LeadProfileDrawer } from '../components/LeadProfileDrawer';
import { leadsApi } from '../lib/api';
import { format } from 'date-fns';
import type { Lead, LeadStatus, LeadSource } from '../types';

const STATUS_OPTIONS: LeadStatus[] = ['new', 'contacted', 'qualified', 'converted', 'lost'];
const SOURCE_OPTIONS: LeadSource[] = ['web_form', 'referral', 'linkedin', 'event', 'cold', 'other'];

// Format currency
const formatCurrency = (value: number | null | undefined) => {
  if (!value) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Format compact currency
const formatCompactCurrency = (value: number | null | undefined) => {
  if (!value) return '$0';
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return formatCurrency(value);
};

export function Leads() {
  const queryClient = useQueryClient();
  const [callbacksOnly, setCallbacksOnly] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [sourceFilter, setSourceFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLead, setSelectedLead] = useState<{ id: string; name: string } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['leads', statusFilter, sourceFilter, callbacksOnly],
    queryFn: () =>
      leadsApi.list({
        status: statusFilter || undefined,
        source: sourceFilter || undefined,
        limit: 100,
        callbacks: callbacksOnly ? 'true' : undefined,
      }),
  });

  const updateLeadMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: LeadStatus }) =>
      leadsApi.update(id, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
  });

  const convertToDealMutation = useMutation({
    mutationFn: (id: string) => leadsApi.convertToDeal(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['deals'] });
    },
  });

  const leads = (data?.leads || []) as Lead[];

  // Filter by search query
  const filteredLeads = leads.filter((lead) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      lead.contact?.name?.toLowerCase().includes(query) ||
      lead.contact?.company?.toLowerCase().includes(query) ||
      lead.contact?.email?.toLowerCase().includes(query)
    );
  });

  // Calculate stats
  const stats = {
    total: leads.length,
    new: leads.filter((l) => l.status === 'new').length,
    qualified: leads.filter((l) => l.status === 'qualified').length,
    totalValue: leads.reduce((sum, l) => sum + (l.value || 0), 0),
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--asi-text)] tracking-tight">
            Leads
          </h1>
          <p className="text-[var(--asi-text-secondary)] mt-1">
            Track and manage incoming leads
          </p>
        </div>
        <Button icon={<Plus className="w-4 h-4" />}>Add Lead</Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="animate-fade-in-up delay-75">
          <StatCard
            title="Total Leads"
            value={stats.total}
            icon={<Users className="w-5 h-5" />}
          />
        </div>
        <div className="animate-fade-in-up delay-150">
          <StatCard
            title="New Leads"
            value={stats.new}
            icon={<Sparkles className="w-5 h-5" />}
            variant="accent"
          />
        </div>
        <div className="animate-fade-in-up delay-225">
          <StatCard
            title="Qualified"
            value={stats.qualified}
            icon={<TrendingUp className="w-5 h-5" />}
          />
        </div>
        <div className="animate-fade-in-up delay-300">
          <StatCard
            title="Potential Value"
            value={formatCompactCurrency(stats.totalValue)}
            icon={<DollarSign className="w-5 h-5" />}
          />
        </div>
      </div>

      <button type="button" aria-pressed={callbacksOnly} onClick={() => setCallbacksOnly(!callbacksOnly)}
        className="px-4 py-2 border rounded-xl text-[var(--asi-text)]">
        {callbacksOnly ? 'Showing open callbacks · Show all leads' : 'View open callbacks'}
      </button>
      {callbacksOnly && <p className="text-[var(--asi-text-secondary)]">Oldest requests first. Callback deadlines appear in Tasks and your morning brief.</p>}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 animate-fade-in-up">
        <div className="relative flex-1 min-w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--asi-text-muted)]" />
          <input
            type="text"
            placeholder="Search leads..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-[var(--asi-bg)] border border-[var(--asi-bg-tertiary)] rounded-xl text-[var(--asi-text)] placeholder:text-[var(--asi-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--asi-accent)] focus:border-transparent transition-all"
          />
        </div>

        <select
          disabled={callbacksOnly}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2.5 bg-[var(--asi-bg)] border border-[var(--asi-bg-tertiary)] rounded-xl text-[var(--asi-text)] focus:outline-none focus:ring-2 focus:ring-[var(--asi-accent)] cursor-pointer transition-all"
        >
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map((status) => (
            <option key={status} value={status}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </option>
          ))}
        </select>

        <select
          disabled={callbacksOnly}
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          className="px-4 py-2.5 bg-[var(--asi-bg)] border border-[var(--asi-bg-tertiary)] rounded-xl text-[var(--asi-text)] focus:outline-none focus:ring-2 focus:ring-[var(--asi-accent)] cursor-pointer transition-all"
        >
          <option value="">All Sources</option>
          {SOURCE_OPTIONS.map((source) => (
            <option key={source} value={source}>
              {source.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
            </option>
          ))}
        </select>
      </div>

      {/* Lead Profile Drawer */}
      <LeadProfileDrawer
        leadId={selectedLead?.id || null}
        leadName={selectedLead?.name}
        onClose={() => setSelectedLead(null)}
      />

      {/* Leads Table */}
      <Card className="animate-fade-in-up overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--asi-bg-tertiary)]">
                <th className="text-left py-4 px-5 text-xs font-medium text-[var(--asi-text-secondary)] uppercase tracking-wider">
                  Lead
                </th>
                <th className="text-left py-4 px-5 text-xs font-medium text-[var(--asi-text-secondary)] uppercase tracking-wider">
                  Company
                </th>
                <th className="text-left py-4 px-5 text-xs font-medium text-[var(--asi-text-secondary)] uppercase tracking-wider">
                  Source
                </th>
                <th className="text-left py-4 px-5 text-xs font-medium text-[var(--asi-text-secondary)] uppercase tracking-wider">
                  Score
                </th>
                <th className="text-left py-4 px-5 text-xs font-medium text-[var(--asi-text-secondary)] uppercase tracking-wider">
                  Value
                </th>
                <th className="text-left py-4 px-5 text-xs font-medium text-[var(--asi-text-secondary)] uppercase tracking-wider">
                  Status
                </th>
                <th className="text-left py-4 px-5 text-xs font-medium text-[var(--asi-text-secondary)] uppercase tracking-wider">
                  Created
                </th>
                <th className="text-right py-4 px-5 text-xs font-medium text-[var(--asi-text-secondary)] uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--asi-bg-tertiary)]">
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center">
                    <div className="w-6 h-6 mx-auto border-2 border-[var(--asi-accent)] border-t-transparent rounded-full animate-spin" />
                  </td>
                </tr>
              ) : filteredLeads.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-[var(--asi-bg-tertiary)] flex items-center justify-center">
                      <Users className="w-6 h-6 text-[var(--asi-text-muted)]" />
                    </div>
                    <p className="text-[var(--asi-text-secondary)]">No leads found</p>
                    <p className="text-sm text-[var(--asi-text-muted)] mt-1">
                      Try adjusting your filters
                    </p>
                  </td>
                </tr>
              ) : (
                filteredLeads.map((lead, idx) => (
                  <tr
                    key={lead.id}
                    className="hover:bg-[var(--asi-bg-secondary)] transition-colors animate-slide-in"
                    style={{ animationDelay: `${idx * 30}ms` }}
                  >
                    <td className="py-4 px-5">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[var(--asi-accent-light)] rounded-xl flex items-center justify-center">
                          <span className="text-[var(--asi-text)] font-semibold text-sm">
                            {lead.contact?.name?.[0]?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-[var(--asi-text)]">
                            {String(lead.metadata?.callback_name || lead.contact?.name || 'Unknown')}
                          </p>
                          {lead.metadata?.request_type === 'callback' && <div className="text-sm text-[var(--asi-text-secondary)]">
                            <p>Warm callback · {String(lead.metadata.callback_phone || lead.contact?.phone || '')}</p>
                            <p>{String(lead.metadata.article_title || 'Blog enquiry')}</p>
                            <p className="max-w-xs break-words">{String(lead.metadata.article_url || '')}</p>
                          </div>}
                          {lead.contact?.email && (
                            <p className="text-sm text-[var(--asi-text-muted)]">
                              {lead.contact.email}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-5">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-[var(--asi-text-muted)]" />
                        <span className="text-[var(--asi-text)]">
                          {lead.contact?.company || '-'}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-5">
                      <Badge variant="default" size="sm">
                        {lead.source?.replace('_', ' ') || 'Unknown'}
                      </Badge>
                    </td>
                    <td className="py-4 px-5">
                      {lead.score !== null ? (
                        <div className="flex items-center gap-1.5">
                          <Star className="w-4 h-4 text-[var(--asi-warning)] fill-[var(--asi-warning)]" />
                          <span className="font-medium text-[var(--asi-text)]">
                            {lead.score}
                          </span>
                        </div>
                      ) : (
                        <span className="text-[var(--asi-text-muted)]">-</span>
                      )}
                    </td>
                    <td className="py-4 px-5">
                      <span className="font-semibold text-[var(--asi-success)]">
                        {formatCurrency(lead.value)}
                      </span>
                    </td>
                    <td className="py-4 px-5">
                      <select
                        value={lead.status}
                        onChange={(e) =>
                          updateLeadMutation.mutate({
                            id: lead.id,
                            status: e.target.value as LeadStatus,
                          })
                        }
                        className={`px-2.5 py-1 rounded-full text-xs font-medium border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[var(--asi-accent)] ${
                          lead.status === 'new'
                            ? 'bg-[var(--asi-info-light)] text-[var(--asi-info)]'
                            : lead.status === 'contacted'
                            ? 'bg-[var(--asi-warning-light)] text-[var(--asi-warning)]'
                            : lead.status === 'qualified'
                            ? 'bg-[var(--asi-accent-light)] text-[var(--asi-text)]'
                            : lead.status === 'converted'
                            ? 'bg-[var(--asi-success-light)] text-[var(--asi-success)]'
                            : 'bg-[var(--asi-error-light)] text-[var(--asi-error)]'
                        }`}
                      >
                        {STATUS_OPTIONS.map((status) => (
                          <option key={status} value={status}>
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-4 px-5 text-sm text-[var(--asi-text-secondary)]">
                      {format(new Date(lead.created_at), 'MMM d, yyyy')}
                    </td>
                    <td className="py-4 px-5">
                      <div className="flex items-center justify-end gap-1">
                        {lead.status !== 'converted' && (
                          <button
                            onClick={() => convertToDealMutation.mutate(lead.id)}
                            disabled={convertToDealMutation.isPending}
                            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-[var(--asi-accent)] text-white hover:bg-[var(--asi-accent-hover)] disabled:opacity-50 transition-all"
                            title="Convert to Deal"
                          >
                            <ArrowRightCircle className="w-3.5 h-3.5" />
                            Deal
                          </button>
                        )}
                        {lead.contact?.email && (
                          <IconButton
                            icon={<Mail className="w-4 h-4" />}
                            label="Send email"
                            size="sm"
                          />
                        )}
                        {lead.contact?.phone && (
                          <IconButton
                            icon={<Phone className="w-4 h-4" />}
                            label="Call"
                            size="sm"
                          />
                        )}
                        <IconButton
                          icon={<FileText className="w-4 h-4" />}
                          label="View profile"
                          size="sm"
                          onClick={() => setSelectedLead({
                            id: lead.id,
                            name: lead.contact?.company || lead.contact?.name || 'Lead'
                          })}
                        />
                        <IconButton
                          icon={<ArrowUpRight className="w-4 h-4" />}
                          label="View details"
                          size="sm"
                        />
                        <IconButton
                          icon={<MoreHorizontal className="w-4 h-4" />}
                          label="More options"
                          size="sm"
                        />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
